// Secure Auth Service - Uses API endpoints instead of direct Supabase calls
// This prevents exposing database credentials and operations to the frontend

import { UserProfile } from '../lib/supabase';

export class SecureAuthService {
  private async getAuthToken(): Promise<string> {
    // Only use Supabase tokens for security - NO localStorage tokens
    try {
      const { supabase } = await import('../lib/supabase');
      if (supabase) {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (session?.access_token && !error) {
          return session.access_token;
        }
      }
    } catch (error) {
      console.log('Supabase not available');
    }

    throw new Error('Authentication required - please sign in with Supabase');
  }

  /**
   * Get user profile using secure API endpoint
   */
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const authToken = await this.getAuthToken();
      const response = await fetch('/api/auth/profile', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get profile');
      }

      return data.profile;
    } catch (error) {
      console.error('Error getting profile via API:', error);
      return null;
    }
  }

  /**
   * Update user profile using secure API endpoint
   */
  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<UserProfile | null> {
    try {
      const authToken = await this.getAuthToken();
      const response = await fetch('/api/auth/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(updates)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update profile');
      }

      return data.profile;
    } catch (error) {
      console.error('Error updating profile via API:', error);
      return null;
    }
  }

  /**
   * Create user profile using secure API endpoint
   */
  async createUserProfile(userId: string, email: string, fullName: string): Promise<void> {
    try {
      const authToken = await this.getAuthToken();
      const response = await fetch('/api/auth/profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ email, full_name: fullName })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create profile');
      }
    } catch (error) {
      console.error('Error creating profile via API:', error);
      throw error;
    }
  }

  /**
   * Update AI chat count for rate limiting (LIFETIME count, not daily)
   */
  async updateAIChatCount(userId: string, increment: number = 1): Promise<boolean> {
    try {
      // Get current profile
      const profile = await this.getUserProfile(userId);
      if (!profile) return false;

      // LIFETIME count - no daily reset, just increment
      const newCount = profile.ai_chat_count + increment;

      // Update the profile with new lifetime count
      const updated = await this.updateUserProfile(userId, {
        ai_chat_count: newCount,
        ai_chat_last_reset: new Date().toISOString() // Track when last used, but don't reset count
      });

      return !!updated;
    } catch (error) {
      console.error('Error updating AI chat count:', error);
      return false;
    }
  }

  /**
   * Check if user has reached AI chat limit (LIFETIME limit, not daily)
   */
  async checkAIChatLimit(userId: string, limit: number = 5): Promise<{ canChat: boolean; remaining: number }> {
    const profile = await this.getUserProfile(userId);
    if (!profile) {
      return { canChat: false, remaining: 0 };
    }

    // LIFETIME count - no daily reset
    const currentCount = profile.ai_chat_count || 0;
    const remaining = Math.max(0, limit - currentCount);
    const canChat = currentCount < limit;

    console.log('🔍 Supabase rate limit check:', {
      userId,
      currentCount,
      limit,
      remaining,
      canChat
    });

    return { canChat, remaining };
  }

  /**
   * Check if Supabase is configured (for auth operations only)
   */
  isConfigured(): boolean {
    try {
      const { isSupabaseConfigured } = require('../lib/supabase');
      return isSupabaseConfigured();
    } catch (error) {
      return false;
    }
  }

  /**
   * Auth operations still use direct Supabase (these are secure)
   */
  async signUp(email: string, password: string, fullName: string) {
    const { supabaseAuthService } = await import('./supabaseAuthService');
    return supabaseAuthService.signUp(email, password, fullName);
  }

  async signIn(email: string, password: string) {
    const { supabaseAuthService } = await import('./supabaseAuthService');
    return supabaseAuthService.signIn(email, password);
  }

  async signOut() {
    const { supabaseAuthService } = await import('./supabaseAuthService');
    return supabaseAuthService.signOut();
  }

  async getSession() {
    const { supabaseAuthService } = await import('./supabaseAuthService');
    return supabaseAuthService.getSession();
  }

  onAuthStateChange(callback: (event: string, session: any) => void) {
    const { supabaseAuthService } = require('./supabaseAuthService');
    return supabaseAuthService.onAuthStateChange(callback);
  }
}

export const secureAuthService = new SecureAuthService(); 